import * as XLSX from "xlsx";
import { formatDateOnly } from "./licenseCertificateTables";

const COMPETENT_ADVISER_FIELDS = [
  {
    key: "rriarTest",
    exportLabel:
      "Regulated Retail Investment Adviser Re-Evaluation (RRIAR) Test",
  },
  {
    key: "vct",
    exportLabel: "VCT Test",
  },
  {
    key: "longTermCareTest",
    exportLabel: "Long Term Care Test",
  },
  {
    key: "equityReleaseTest",
    exportLabel: "Equity Release Test",
  },
  {
    key: "consumerDutyTest",
    exportLabel: "Consumer Duty Test",
    accountsForExpiry: false,
  },
];

const getStatusLabel = (row = {}) => {
  if (row.statusClass === "expired") {
    return "Expired";
  }

  if (row.statusClass === "noDate") {
    return "No Date";
  }

  if (row.lastFollowUpSentAt || row.lastFollowUp === "-") {
    if (row.lastFollowUpSentAt) {
      return "Follow Up Sent";
    }
  }

  if (row.lastFollowUp && row.lastFollowUp !== "-") {
    return "Follow Up Sent";
  }

  if (row.statusClass === "dueSoon") {
    return "Expiring Soon";
  }

  return "Active";
};

const formatExportDate = (value) => {
  if (!value) return "";

  const formattedDate = formatDateOnly(value);

  return formattedDate === "-" ? "" : formattedDate || "";
};

const formatReminderDates = (reminderDates = []) => {
  if (!Array.isArray(reminderDates)) return "";

  return reminderDates
    .map((date) => formatExportDate(date))
    .filter(Boolean)
    .join(", ");
};

export const buildCompetentAdviserTableRows = (rows = []) => {
  const groupedRows = new Map();

  rows.forEach((row) => {
    const groupKey =
      row.adviserEmail || row.adviserName || row.adviserId || row.id;

    if (!groupKey) return;

    if (!groupedRows.has(groupKey)) {
      groupedRows.set(groupKey, {
        id: `competent-adviser-${groupKey}`,
        adviserId: row.adviserId || "",
        adviserName: row.adviserName || "",
        adviserEmail: row.adviserEmail || "",
        adviserBusinessStatus: row.adviserBusinessStatus || "",
        category: "Competent Adviser",
        status: "No date",
        statusClass: "noDate",
        daysUntil: null,
        deadline: "",
        reminderDates: [],
        lastFollowUp: "-",
        lastFollowUpLabel: "No follow-up has been sent yet.",
        updatedLabel: row.updatedLabel || "",
      });
    }

    const adviserRow = groupedRows.get(groupKey);

    adviserRow[row.testKey] = row.testDate || row.deadline;
    adviserRow[`${row.testKey}Meta`] = row;
  });

  groupedRows.forEach((adviserRow) => {
    const expiryRows = COMPETENT_ADVISER_FIELDS.filter(
      (field) => field.accountsForExpiry !== false,
    )
      .map((field) => adviserRow[`${field.key}Meta`])
      .filter(Boolean);

    const urgentRows = expiryRows
      .filter((row) => typeof row.daysUntil === "number")
      .sort((a, b) => a.daysUntil - b.daysUntil);

    const statusSource = urgentRows[0] || expiryRows[0];

    if (!statusSource) return;

    adviserRow.status = statusSource.status;
    adviserRow.statusClass = statusSource.statusClass;
    adviserRow.daysUntil = statusSource.daysUntil;
    adviserRow.deadline = statusSource.deadline;
    adviserRow.reminderDates = statusSource.reminderDates || [];
    adviserRow.lastFollowUp = statusSource.lastFollowUp;
    adviserRow.lastFollowUpSentAt = statusSource.lastFollowUpSentAt;
    adviserRow.lastFollowUpLabel = statusSource.lastFollowUpLabel;
    adviserRow.lastFollowUpPeriod = statusSource.lastFollowUpPeriod;
    adviserRow.lastFollowUpSubject = statusSource.lastFollowUpSubject;
    adviserRow.lastFollowUpFromName = statusSource.lastFollowUpFromName;
    adviserRow.lastFollowUpFromEmail = statusSource.lastFollowUpFromEmail;
    adviserRow.nextFollowUpSendAt = statusSource.nextFollowUpSendAt;
    adviserRow.updatedLabel = statusSource.updatedLabel;
    adviserRow.statusSourceLabel =
      statusSource.statusSourceLabel ||
      statusSource.testName ||
      statusSource.deadlineLabel ||
      "Competent Adviser";
  });

  return Array.from(groupedRows.values());
};

const createDplRows = (rows = []) => {
  return rows.map((row) => ({
    "Adviser Name": row.adviserName || "",
    "Adviser Email": row.adviserEmail || "",
    "DPL Name": row.dplName || "",
    "DPL Registration Number": row.dplRegistrationNumber || "",
    "DPL Expiry Date": formatExportDate(row.deadline),
    Status: getStatusLabel(row),
    "Days Until Expiry": typeof row.daysUntil === "number" ? row.daysUntil : "",
    "Reminder Dates": formatReminderDates(row.reminderDates),
    "Last Follow Up": formatExportDate(
      row.lastFollowUpSentAt || row.lastFollowUp,
    ),
    "Last Follow Up Subject": row.lastFollowUpSubject || "",
    "Last Follow Up From Name": row.lastFollowUpFromName || "",
    "Last Follow Up From Email": row.lastFollowUpFromEmail || "",
    "Next Follow Up Date": formatExportDate(row.nextFollowUpSendAt),
  }));
};

const createSpsRows = (rows = []) => {
  return rows.map((row) => ({
    "Adviser Name": row.adviserName || "",
    "Adviser Email": row.adviserEmail || "",
    "SPS Accredited Body": row.spsAccreditedBody || "",
    "SPS Expiry Date": formatExportDate(row.deadline),
    Status: getStatusLabel(row),
    "Days Until Expiry": typeof row.daysUntil === "number" ? row.daysUntil : "",
    "Reminder Dates": formatReminderDates(row.reminderDates),
    "Last Follow Up": formatExportDate(
      row.lastFollowUpSentAt || row.lastFollowUp,
    ),
    "Last Follow Up Subject": row.lastFollowUpSubject || "",
    "Last Follow Up From Name": row.lastFollowUpFromName || "",
    "Last Follow Up From Email": row.lastFollowUpFromEmail || "",
    "Next Follow Up Date": formatExportDate(row.nextFollowUpSendAt),
  }));
};

export const createFitAndProperRows = (
  rows = [],
  { includeCreditHistory = false } = {},
) => {
  return rows.map((row) => ({
    "Adviser Name": row.adviserName || "",
    "Adviser Email": row.adviserEmail || "",
    "Fit & Proper Expiry Date": formatExportDate(row.deadline),
    ...(includeCreditHistory
      ? {
          "Credit Score": row.creditScore || "",
          "Credit Score Date": formatExportDate(row.creditScoreDate),
          "Credit Report Date": formatExportDate(row.creditReportDate),
        }
      : {}),
    CC: Array.isArray(row.ccAdvisers) ? row.ccAdvisers.join(", ") : "",
    Notes: row.notes || "",
    Status: getStatusLabel(row),
    "Days Until Expiry": typeof row.daysUntil === "number" ? row.daysUntil : "",
    "Reminder Dates": formatReminderDates(row.reminderDates),
    "Last Follow Up": formatExportDate(
      row.lastFollowUpSentAt || row.lastFollowUp,
    ),
    "Last Follow Up Subject": row.lastFollowUpSubject || "",
    "Last Follow Up From Name": row.lastFollowUpFromName || "",
    "Last Follow Up From Email": row.lastFollowUpFromEmail || "",
    "Next Follow Up Date": formatExportDate(row.nextFollowUpSendAt),
  }));
};

const createCompetentAdviserRows = (rows = []) => {
  const groupedRows = buildCompetentAdviserTableRows(rows);

  return groupedRows.map((row) => ({
    "Adviser Name": row.adviserName || "",
    "Adviser Email": row.adviserEmail || "",
    "RRIAR Test": formatExportDate(row.rriarTest),
    "VCT Test": formatExportDate(row.vct),
    "Long Term Care Test": formatExportDate(row.longTermCareTest),
    "Equity Release Test": formatExportDate(row.equityReleaseTest),
    "Consumer Duty Test": formatExportDate(row.consumerDutyTest),
    Status: getStatusLabel(row),
    "Nearest Deadline": formatExportDate(row.deadline),
    "Days Until Deadline":
      typeof row.daysUntil === "number" ? row.daysUntil : "",
    "Reminder Dates": formatReminderDates(row.reminderDates),
    "Last Follow Up": formatExportDate(
      row.lastFollowUpSentAt || row.lastFollowUp,
    ),
    "Last Follow Up Subject": row.lastFollowUpSubject || "",
    "Last Follow Up From Name": row.lastFollowUpFromName || "",
    "Last Follow Up From Email": row.lastFollowUpFromEmail || "",
    "Next Follow Up Date": formatExportDate(row.nextFollowUpSendAt),
  }));
};

const createTcRows = (rows = []) => {
  return rows.map((row) => ({
    "Adviser Name": row.adviserName || "",
    "Adviser Email": row.adviserEmail || "",
    "Appointment Date": formatExportDate(row.appointmentDate),
    "Pre-CAS Sign Off": formatExportDate(row.preCASSignOff),
    "Last OCA Meeting": formatExportDate(row.lastOcaMeeting),
    "Next OCA Meeting": formatExportDate(row.nextOcaMeeting),
    Status: getStatusLabel(row),
    "Days Until Deadline":
      typeof row.daysUntil === "number" ? row.daysUntil : "",
    "Reminder Dates": formatReminderDates(row.reminderDates),
    "Last Follow Up": formatExportDate(
      row.lastFollowUpSentAt || row.lastFollowUp,
    ),
    "Last Follow Up Subject": row.lastFollowUpSubject || "",
    "Last Follow Up From Name": row.lastFollowUpFromName || "",
    "Last Follow Up From Email": row.lastFollowUpFromEmail || "",
    "Next Follow Up Date": formatExportDate(row.nextFollowUpSendAt),
  }));
};

const setColumnWidths = (worksheet, rows = []) => {
  const headers = Object.keys(rows[0] || {});

  worksheet["!cols"] = headers.map((header) => {
    const longestValueLength = rows.reduce((longest, row) => {
      const valueLength = String(row[header] ?? "").length;
      return Math.max(longest, valueLength);
    }, header.length);

    return {
      wch: Math.min(longestValueLength + 2, 50),
    };
  });
};

const createWorksheet = (rows = []) => {
  const worksheet = XLSX.utils.json_to_sheet(rows);

  setColumnWidths(worksheet, rows);

  if (worksheet["!ref"]) {
    worksheet["!autofilter"] = {
      ref: worksheet["!ref"],
    };
  }

  return worksheet;
};

const getFileDate = () => {
  const today = new Date();

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

export const downloadFitAndProperExcel = (fitAndProperRows = []) => {
  const rows = createFitAndProperRows(
    fitAndProperRows,
    { includeCreditHistory: true },
  );

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    createWorksheet(rows),
    "Fit and Proper",
  );
  XLSX.writeFile(
    workbook,
    `Fit-and-Proper-${getFileDate()}.xlsx`,
    { compression: true },
  );
};

export const downloadLicenseCertificatesExcel = (
  data,
  { includeCreditHistory = false } = {},
) => {
  if (!data) {
    throw new Error("No licence certificate data is available to export.");
  }

  const workbook = XLSX.utils.book_new();

  const sheets = [
    {
      name: "DPL Certificate",
      rows: createDplRows(data.dplRows || []),
    },
    {
      name: "SPS Certificate",
      rows: createSpsRows(data.spsRows || []),
    },
    {
      name: "Fit and Proper",
      rows: createFitAndProperRows(data.fitAndProperRows || [], {
        includeCreditHistory,
      }),
    },
    {
      name: "Competent Adviser",
      rows: createCompetentAdviserRows(data.competentAdviserRows || []),
    },
    {
      name: "T&C",
      rows: createTcRows(data.tcRows || []),
    },
  ];

  sheets.forEach(({ name, rows }) => {
    const worksheet = createWorksheet(rows);

    XLSX.utils.book_append_sheet(workbook, worksheet, name);
  });

  XLSX.writeFile(workbook, `Licence-Certificates-${getFileDate()}.xlsx`, {
    compression: true,
  });
};
